import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import KAnonymityNotice from '../components/KAnonymityNotice';
import { useOrg } from '../context/OrgContext';

function OrgInsightsDashboard() {
  const navigate = useNavigate();
  const params = useParams();
  const { orgSlug } = useOrg();
  const slug = params.slug || orgSlug;
  const [orgId, setOrgId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [metrics, setMetrics] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [sentiment, setSentiment] = useState(null);

  useEffect(() => {
    fetchOrgId();
  }, []);

  useEffect(() => {
    if (orgId) {
      fetchAllData();
    }
  }, [orgId]);

  const fetchOrgId = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get('/api/org/onboard/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const memberships = res.data.memberships || [];
      if (memberships.length > 0) {
        setOrgId(memberships[0].org_id);
      } else {
        setError('No organization found.');
      }
    } catch (err) {
      setError('Failed to load organization info.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [metricsRes, heatmapRes, sentimentRes] = await Promise.all([
        axios.get(`/api/org-admin/insights/metrics?orgId=${orgId}`, { headers }),
        axios.get(`/api/org-admin/insights/heatmap?orgId=${orgId}`, { headers }),
        axios.get(`/api/org-admin/insights/sentiment?orgId=${orgId}`, { headers }),
      ]);
      setMetrics(metricsRes.data);
      setHeatmap(heatmapRes.data);
      setSentiment(sentimentRes.data);
    } catch (err) {
      console.error('Failed to fetch insights data:', err);
    }
  };

  const getCurrentQuarter = () => {
    const now = new Date();
    const q = Math.ceil((now.getMonth() + 1) / 3);
    return { quarter: q, year: now.getFullYear() };
  };

  const handleDownloadReport = () => {
    const { quarter, year } = getCurrentQuarter();
    const token = localStorage.getItem('accessToken');
    window.open(
      `/api/org-admin/insights/report?orgId=${orgId}&quarter=${quarter}&year=${year}&token=${token}`,
      '_blank'
    );
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading insights...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.errorCard}>
          <p style={styles.errorText}>{error}</p>
          <button onClick={() => navigate(slug ? `/org/${slug}/admin` : '/org/admin')} style={styles.backBtn}>
            ← Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📈 Insights Dashboard</h1>
          <p style={styles.subtitle}>Aggregated, anonymized community health metrics</p>
        </div>
        <div style={styles.headerActions}>
          <button onClick={handleDownloadReport} style={styles.downloadBtn}>
            📥 Download Quarterly Report
          </button>
          <button onClick={() => navigate(slug ? `/org/${slug}/admin` : '/org/admin')} style={styles.backLink}>
            ← Back to Admin
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Key Metrics</h2>
        {metrics?.suppressed ? (
          <KAnonymityNotice />
        ) : (
          <div style={styles.metricGrid}>
            <MetricCard
              label="Active Participants"
              value={metrics?.activeParticipants ?? metrics?.active_participants_30d ?? '—'}
              subtitle="Last 30 days"
            />
            <MetricCard
              label="Passive Participants"
              value={metrics?.passiveParticipants ?? metrics?.passive_participants_30d ?? '—'}
              subtitle="Viewed but didn't post"
            />
            <MetricCard
              label="Active Spaces"
              value={metrics?.activeChannels ?? metrics?.active_channels_30d ?? '—'}
              subtitle="With recent activity"
            />
            <MetricCard
              label="Engagement Rate"
              value={
                metrics?.engagementRate != null
                  ? `${(metrics.engagementRate * 100).toFixed(1)}%`
                  : metrics?.engagement_rate != null
                  ? `${(metrics.engagement_rate * 100).toFixed(1)}%`
                  : '—'
              }
              subtitle="Posts + comments / participants"
            />
            <MetricCard
              label="MoM Growth"
              value={
                metrics?.momGrowth != null
                  ? `${(metrics.momGrowth * 100).toFixed(1)}%`
                  : metrics?.mom_growth_rate != null
                  ? `${(metrics.mom_growth_rate * 100).toFixed(1)}%`
                  : metrics?.mom_growth != null
                  ? `${(metrics.mom_growth * 100).toFixed(1)}%`
                  : '—'
              }
              subtitle="Month-over-month"
            />
          </div>
        )}
      </section>

      {/* Topic Heatmap */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Topic Heatmap</h2>
        <TopicHeatmapTable data={heatmap} />
      </section>

      {/* Sentiment Trends */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Sentiment Trends</h2>
        <SentimentTable data={sentiment} />
      </section>
    </div>
  );
}

/* ---- Sub-components ---- */

function MetricCard({ label, value, subtitle }) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricValue}>{value}</div>
      <div style={styles.metricLabel}>{label}</div>
      {subtitle && <div style={styles.metricSubtitle}>{subtitle}</div>}
    </div>
  );
}

function TopicHeatmapTable({ data }) {
  const categories = data?.categories || data || [];

  if (!Array.isArray(categories) || categories.length === 0) {
    return <p style={styles.emptyText}>No topic data available yet.</p>;
  }

  return (
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Category</th>
            <th style={styles.th}>Posts</th>
            <th style={styles.th}>Contributors</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat, i) => (
            <tr key={i}>
              <td style={styles.td}>{cat.category_name || cat.name}</td>
              <td style={styles.td}>
                {cat.suppressed ? (
                  <span style={styles.suppressedText}>Insufficient participants</span>
                ) : (
                  cat.post_count ?? cat.postCount ?? '—'
                )}
              </td>
              <td style={styles.td}>
                {cat.suppressed ? (
                  <span style={styles.suppressedText}>—</span>
                ) : (
                  cat.contributor_count ?? cat.contributorCount ?? '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SentimentTable({ data }) {
  const weeks = data?.weeks || data?.sentiment || data || [];

  if (!Array.isArray(weeks) || weeks.length === 0) {
    return <p style={styles.emptyText}>No sentiment data available yet.</p>;
  }

  return (
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Week</th>
            <th style={styles.th}>Avg Sentiment</th>
            <th style={styles.th}>Contributors</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, i) => (
            <tr key={i}>
              <td style={styles.td}>
                {week.week_start
                  ? new Date(week.week_start).toLocaleDateString()
                  : week.week || '—'}
              </td>
              <td style={styles.td}>
                {week.suppressed ? (
                  <span style={styles.suppressedText}>Suppressed</span>
                ) : week.avg_sentiment != null ? (
                  week.avg_sentiment.toFixed(2)
                ) : (
                  '—'
                )}
              </td>
              <td style={styles.td}>
                {week.suppressed ? '—' : (week.contributor_count ?? week.contributors ?? '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---- Styles ---- */

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    padding: '32px 40px',
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 4px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  downloadBtn: {
    padding: '10px 20px',
    backgroundColor: '#7C3AED',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  backLink: {
    background: 'none',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '13px',
    color: '#374151',
    cursor: 'pointer',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
    margin: '0 0 16px 0',
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
  metricCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  },
  metricValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#7C3AED',
    marginBottom: '4px',
  },
  metricLabel: {
    fontSize: '13px',
    color: '#374151',
    fontWeight: '600',
  },
  metricSubtitle: {
    fontSize: '11px',
    color: '#9ca3af',
    marginTop: '4px',
  },
  tableWrapper: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid #f3f4f6',
    backgroundColor: '#fafafa',
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #f3f4f6',
  },
  suppressedText: {
    color: '#d97706',
    fontSize: '12px',
    fontStyle: 'italic',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6b7280',
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
  },
  loadingText: {
    fontSize: '16px',
    color: '#6b7280',
  },
  errorCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  errorText: {
    fontSize: '15px',
    color: '#dc2626',
    marginBottom: '16px',
  },
  backBtn: {
    padding: '10px 20px',
    backgroundColor: '#7C3AED',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default OrgInsightsDashboard;
